/**
 * The React face of the dock's IIR queue (iirQueue.ts). Same shape and reasons
 * as useQueue.ts: identity is the live MSAL account (the stamp and the bearer
 * from one source), replay on boot, on an account change (how a held inspection
 * goes home) and when the network returns.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { acquireApiToken, currentUpn } from '../config/auth';
import {
  createIirDraftKeeper,
  createIirQueue,
  indexedDbIirDraftStore,
  type IirContext,
  type IirDraftKeeper,
  type IirDraftRow,
  type IirQueue,
  type IirSnapshot,
  type PhotoInput,
} from './iirQueue';

let singleton: IirQueue | null = null;

/** Built on first use, never at import: it opens IndexedDB. */
export function iirQueue(): IirQueue {
  singleton ??= createIirQueue({
    getUpn: currentUpn,
    getToken: async () => {
      try {
        return await acquireApiToken();
      } catch {
        return null;
      }
    },
  });
  return singleton;
}

const EMPTY: IirSnapshot = { pending: null, held: [], refused: [], results: [], photosPending: null, photosFailed: [], photosHeld: 0 };

export function useIirQueue(signedInUpn: string, queue: IirQueue = iirQueue()) {
  // Starts unknown (null), not zero: the first paint must not claim an empty queue.
  const [snap, setSnap] = useState<IirSnapshot>(EMPTY);

  useEffect(() => {
    const unsubscribe = queue.subscribe(setSnap);
    void queue.snapshot().then(setSnap);
    return unsubscribe;
  }, [queue]);

  useEffect(() => {
    void queue.replay();
  }, [queue, signedInUpn]);

  useEffect(() => {
    const onOnline = () => void queue.replay();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [queue]);

  const submit = useCallback(
    async (payload: Record<string, unknown>, context: IirContext, photos: readonly PhotoInput[] = []) => {
      const row = await queue.enqueue(payload, context, photos);
      if (typeof navigator === 'undefined' || navigator.onLine) void queue.replay();
      return row;
    },
    [queue],
  );

  return {
    ...snap,
    submit,
    replay: useCallback(() => queue.replay(), [queue]),
    discard: useCallback((id: number, owner: string) => queue.discard(id, owner), [queue]),
    retry: useCallback((id: number, owner: string) => queue.retry(id, owner), [queue]),
    retryPhoto: useCallback((id: number, owner: string) => queue.retryPhoto(id, owner), [queue]),
    discardPhoto: useCallback((id: number, owner: string) => queue.discardPhoto(id, owner), [queue]),
    acknowledgeResults: useCallback(() => queue.acknowledgeResults(), [queue]),
  };
}

/* ── The half-done inspection (UI/UX audit, 31 Oct 2026, UX-08) ───────────── */

let draftSingleton: IirDraftKeeper | null = null;

/** Built on first use, never at import: it opens IndexedDB. */
export function iirDraftKeeper(): IirDraftKeeper {
  draftSingleton ??= createIirDraftKeeper(indexedDbIirDraftStore());
  return draftSingleton;
}

/**
 * What this tablet holds of the signed-in account's half-done inspection.
 *
 * ⚠ `unreadable` IS NOT `none` (rule 15). A store that could not be read cannot say "you have no
 * unfinished inspection here"; the screen says it could not look.
 */
export type IirDraftState<D> =
  | { status: 'loading' }
  | { status: 'none' }
  | { status: 'unreadable' }
  | { status: 'found'; row: IirDraftRow<D> };

/**
 * Keeps the form's draft on the device as it is typed, and offers back the one kept before.
 *
 * WHY THE WRITE IS DEBOUNCED AND FLUSHED. A put per keystroke re-writes every photo blob on every
 * letter of a rego; 300 ms after the last change is enough. But a debounce on its own loses the
 * last change exactly when it matters, so the pending write is FLUSHED on `pagehide` (a reload, a
 * closed or discarded tab, the sign-in redirect) and when the component unmounts (an area tab).
 *
 * WHY `clear` CANCELS THE PENDING WRITE. Sending the inspection clears the draft; a debounced save
 * still in flight would otherwise land after it and offer an inspection already in the queue back
 * as unfinished - the same truck inspected twice.
 *
 * The owner is passed to `keep` by the caller with each draft rather than read here, so a draft is
 * stamped with whoever OPENED it - never with an account that signed in while it sat on screen.
 */
export function useIirDraft<D>(signedInUpn: string, keeper: IirDraftKeeper = iirDraftKeeper(), delayMs = 300) {
  const [state, setState] = useState<IirDraftState<D>>({ status: 'loading' });
  /** The last write failed: the draft is on screen but NOT on the device. Shown, never hidden. */
  const [saveFailed, setSaveFailed] = useState(false);
  const pending = useRef<{ upn: string; draft: D; label: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const read = useCallback(() => {
    let live = true;
    setState({ status: 'loading' });
    keeper.load(signedInUpn).then(
      (row) => live && setState(row ? { status: 'found', row: row as IirDraftRow<D> } : { status: 'none' }),
      () => live && setState({ status: 'unreadable' }),
    );
    return () => {
      live = false;
    };
  }, [keeper, signedInUpn]);
  useEffect(() => read(), [read]);

  const flush = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    const p = pending.current;
    pending.current = null;
    if (!p) return;
    keeper.save(p.upn, p.draft, p.label).then(
      () => setSaveFailed(false),
      () => setSaveFailed(true),
    );
  }, [keeper]);

  useEffect(() => {
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [flush]);

  const keep = useCallback(
    (owner: string, draft: D, label: string) => {
      pending.current = { upn: owner, draft, label };
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(flush, delayMs);
    },
    [flush, delayMs],
  );

  const clear = useCallback(
    async (owner: string) => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
      pending.current = null;
      setState({ status: 'none' });
      setSaveFailed(false);
      await keeper.clear(owner);
    },
    [keeper],
  );

  return { state, saveFailed, keep, clear, reload: read };
}
